/**
 * Bank Partner Dashboard — MAP-18
 * 
 * Pipeline view for bank partners to manage loan requests.
 * Identified by connected wallet address matching BANK_PARTNERS config.
 */

import { useState, useEffect, useCallback, Fragment } from 'react'
import { useAccount } from 'wagmi'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { Dialog, Transition } from '@headlessui/react'
import { SEO } from '../components/common/SEO'
import { Card, CardContent } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { InfoBox } from '../components/common/InfoBox'
import { formatAddress } from '../utils/format'
import { toast } from 'react-hot-toast'
import {
  WalletConnectIllustration,
  BlockSecureIllustration,
} from '@bitcoin-com/react-components/src/components/mini-illustrations'
import type { LoanRequestStatus } from '../hooks/useLoanRequest'

// Must match server/config.js
const BANK_PARTNERS: Record<string, { name: string; address: string }> = {
  AMDAX: {
    name: 'AMDAX',
    address: '0x717d52e84eF30875De52834603c112675CeDB7CA',
  },
  EMPOWERMINT: {
    name: 'Empowermint',
    address: '0x765365f5DF83A79001229f4E76881976039DB8F5',
  },
}

// Pipeline columns
const PIPELINE_COLUMNS: { key: LoanRequestStatus; label: string; color: string }[] = [
  { key: 'requested', label: 'Pending', color: 'border-yellow-400' },
  { key: 'bank_review', label: 'In Review', color: 'border-blue-400' },
  { key: 'kyc', label: 'KYC', color: 'border-purple-400' },
  { key: 'disbursement', label: 'Disbursement', color: 'border-orange-400' },
  { key: 'active', label: 'Active', color: 'border-green-400' },
  { key: 'repaid', label: 'Repaid', color: 'border-gray-400' },
]

interface Loan {
  id: string
  locId: string | null
  borrowerAddress: string
  bankPartner: string | null
  requestedAmount: number
  status: LoanRequestStatus
  createdAt: string
  updatedAt: string
}

function identifyBank(address: string): { key: string; name: string } | null {
  const lower = address.toLowerCase()
  for (const [key, val] of Object.entries(BANK_PARTNERS)) {
    if (val.address.toLowerCase() === lower) {
      return { key, name: val.name }
    }
  }
  return null
}

export function BankDashboardPage() {
  const { address, isConnected } = useAccount()
  const { openConnectModal } = useConnectModal()

  const bank = address ? identifyBank(address) : null

  const [loans, setLoans] = useState<Loan[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null)
  const [showActionModal, setShowActionModal] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [viewMode, setViewMode] = useState<'pipeline' | 'list'>('pipeline')

  const fetchLoans = useCallback(async () => {
    if (!address) return
    setIsLoading(true)
    try {
      const resp = await fetch(`/api/loans/bank/${address.toLowerCase()}`, {
        signal: AbortSignal.timeout(5000),
      })
      if (resp.ok) {
        const data = await resp.json()
        setLoans(data.loans ?? [])
      }
    } catch {
      // Fallback: try fetching all loans (dev mode)
      try {
        const resp = await fetch('/api/loans/all', { signal: AbortSignal.timeout(5000) })
        if (resp.ok) {
          const data = await resp.json()
          setLoans(data.loans ?? [])
        }
      } catch {
        toast.error('Failed to load loan requests')
      }
    } finally {
      setIsLoading(false)
    }
  }, [address])

  useEffect(() => {
    if (isConnected && address) {
      fetchLoans()
      // Poll every 30s
      const interval = setInterval(fetchLoans, 30000)
      return () => clearInterval(interval)
    }
  }, [isConnected, address, fetchLoans])

  const updateLoanStatus = async (loanId: string, newStatus: LoanRequestStatus) => {
    setIsUpdating(true)
    try {
      const resp = await fetch(`/api/loans/${loanId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, bankAddress: address?.toLowerCase() }),
        signal: AbortSignal.timeout(5000),
      })
      if (resp.ok) {
        toast.success(`Loan moved to ${newStatus}`)
        setShowActionModal(false)
        setSelectedLoan(null)
        await fetchLoans()
      } else {
        const err = await resp.json()
        toast.error(err.error || 'Failed to update')
      }
    } catch {
      toast.error('Failed to update loan status')
    } finally {
      setIsUpdating(false)
    }
  }

  // Not connected
  if (!isConnected) {
    return (
      <>
        <SEO title="Bank Partner Dashboard" description="Manage loan requests as a bank partner." canonical="/bank-dashboard" />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24"><WalletConnectIllustration className="w-full h-full" /></div>
            </div>
            <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-4">Bank Partner Login</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">Connect your bank partner wallet to access the loan pipeline dashboard.</p>
            <Button size="lg" onClick={openConnectModal}>Connect Wallet</Button>
          </div>
        </div>
      </>
    )
  }

  // Connected but not a recognized bank partner
  if (!bank) {
    return (
      <>
        <SEO title="Bank Partner Dashboard" description="Manage loan requests as a bank partner." canonical="/bank-dashboard" />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24"><BlockSecureIllustration className="w-full h-full" /></div>
          </div>
          <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-4">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The connected wallet (<span className="font-mono">{formatAddress(address!)}</span>) is not a registered bank partner.
          </p>
          <InfoBox variant="info" title="Registered Partners">
            <ul className="text-sm space-y-1 text-left">
              {Object.entries(BANK_PARTNERS).map(([key, val]) => (
                <li key={key}>
                  <strong>{val.name}</strong>: <span className="font-mono text-xs">{val.address}</span>
                </li>
              ))}
            </ul>
          </InfoBox>
        </div>
      </>
    )
  }

  // Stats
  const statsByStatus = PIPELINE_COLUMNS.map(col => ({
    ...col,
    count: loans.filter(l => l.status === col.key).length,
    totalAmount: loans.filter(l => l.status === col.key).reduce((sum, l) => sum + l.requestedAmount, 0),
  }))
  const totalPendingValue = loans.filter(l => l.status === 'requested').reduce((s, l) => s + l.requestedAmount, 0)
  const totalActiveValue = loans.filter(l => ['bank_review', 'kyc', 'disbursement', 'active'].includes(l.status)).reduce((s, l) => s + l.requestedAmount, 0)

  return (
    <>
      <SEO title={`${bank.name} — Bank Dashboard`} description="Manage loan pipeline." canonical="/bank-dashboard" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-display font-black text-heading-xl uppercase text-text-primary mb-1">
                {bank.name} Dashboard
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">{address}</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'pipeline' ? 'primary' : 'default'}
                size="sm"
                onClick={() => setViewMode('pipeline')}
              >
                Pipeline
              </Button>
              <Button
                variant={viewMode === 'list' ? 'primary' : 'default'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                List
              </Button>
              <Button variant="default" size="sm" onClick={fetchLoans} disabled={isLoading}>
                {isLoading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Requests</p>
                <p className="text-3xl font-bold text-text-primary">{loans.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pending Review</p>
                <p className="text-3xl font-bold text-yellow-600">{statsByStatus[0]?.count ?? 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pending Value</p>
                <p className="text-3xl font-bold text-brand-orange">${totalPendingValue.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Active Pipeline</p>
                <p className="text-3xl font-bold text-green-600">${totalActiveValue.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          {isLoading && loans.length === 0 ? (
            <div className="flex justify-center py-12"><div className="spinner h-12 w-12" /></div>
          ) : loans.length === 0 ? (
            <Card>
              <CardContent className="text-center py-16">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No Loan Requests Yet</h3>
                <p className="text-gray-600 dark:text-gray-400">Loan requests from borrowers will appear here when submitted.</p>
              </CardContent>
            </Card>
          ) : viewMode === 'pipeline' ? (
            /* Pipeline (Kanban) View */
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-4 min-w-[900px]">
                {PIPELINE_COLUMNS.map(col => {
                  const colLoans = loans.filter(l => l.status === col.key)
                  return (
                    <div key={col.key} className={`flex-1 min-w-[200px] border-t-4 ${col.color} bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3`}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">{col.label}</h3>
                        <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full font-mono">{colLoans.length}</span>
                      </div>
                      <div className="space-y-3">
                        {colLoans.map(loan => (
                          <LoanCard
                            key={loan.id}
                            loan={loan}
                            onAction={() => { setSelectedLoan(loan); setShowActionModal(true) }}
                            compact
                          />
                        ))}
                        {colLoans.length === 0 && (
                          <p className="text-xs text-gray-400 dark:text-gray-600 text-center py-6">Empty</p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            /* List View */
            <div className="space-y-3">
              {loans.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(loan => (
                <LoanCard
                  key={loan.id}
                  loan={loan}
                  onAction={() => { setSelectedLoan(loan); setShowActionModal(true) }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      <Transition appear show={showActionModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => !isUpdating && setShowActionModal(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 p-4 sm:p-6 text-left shadow-xl transition-all">
                  {selectedLoan && (
                    <>
                      <Dialog.Title as="h3" className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                        Loan #{selectedLoan.id.slice(-8)}
                      </Dialog.Title>
                      <div className="space-y-4">
                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Borrower</span>
                            <span className="font-mono">{formatAddress(selectedLoan.borrowerAddress)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Amount</span>
                            <span className="font-bold text-brand-orange">${selectedLoan.requestedAmount.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Status</span>
                            <span className="font-semibold">{selectedLoan.status}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Submitted</span>
                            <span>{new Date(selectedLoan.createdAt).toLocaleDateString()}</span>
                          </div>
                          {selectedLoan.locId && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">LOC</span>
                              <span className="font-mono">#{selectedLoan.locId}</span>
                            </div>
                          )}
                        </div>

                        <div>
                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Move to:</p>
                          <div className="grid grid-cols-2 gap-2">
                            {getNextStatuses(selectedLoan.status).map(next => (
                              <Button
                                key={next.key}
                                variant={next.key === 'rejected' ? 'ghost' : 'default'}
                                size="sm"
                                onClick={() => updateLoanStatus(selectedLoan.id, next.key)}
                                disabled={isUpdating}
                                className={`min-h-[44px] ${next.key === 'rejected' ? 'text-red-600 hover:bg-red-50' : ''}`}
                              >
                                {next.label}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <Button variant="ghost" onClick={() => setShowActionModal(false)} disabled={isUpdating} className="w-full min-h-[44px]">
                          Close
                        </Button>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

// Get valid next statuses for a loan
function getNextStatuses(current: LoanRequestStatus): { key: LoanRequestStatus; label: string }[] {
  const transitions: Record<string, { key: LoanRequestStatus; label: string }[]> = {
    requested: [
      { key: 'bank_review', label: '→ Accept & Review' },
      { key: 'rejected', label: '✕ Reject' },
    ],
    bank_review: [
      { key: 'kyc', label: '→ Send to KYC' },
      { key: 'rejected', label: '✕ Reject' },
    ],
    kyc: [
      { key: 'disbursement', label: '→ Approve & Disburse' },
      { key: 'rejected', label: '✕ Reject' },
    ],
    disbursement: [
      { key: 'active', label: '→ Mark Active' },
    ],
    active: [
      { key: 'repaid', label: '→ Mark Repaid' },
    ],
    repaid: [],
    rejected: [],
  }
  return transitions[current] ?? []
}

// Loan card component
function LoanCard({
  loan,
  onAction,
  compact = false,
}: {
  loan: Loan
  onAction: () => void
  compact?: boolean
}) {
  const statusColors: Record<string, string> = {
    requested: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    bank_review: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    kyc: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    disbursement: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    repaid: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  }

  if (compact) {
    return (
      <button
        onClick={onAction}
        className="w-full text-left bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm border border-gray-200 dark:border-gray-700 hover:border-brand-orange transition-colors"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-lg font-bold text-text-primary">${loan.requestedAmount.toLocaleString()}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColors[loan.status] ?? ''}`}>
            {loan.status}
          </span>
        </div>
        <p className="text-xs text-gray-500 font-mono">{formatAddress(loan.borrowerAddress)}</p>
        <p className="text-xs text-gray-400 mt-1">{new Date(loan.createdAt).toLocaleDateString()}</p>
      </button>
    )
  }

  return (
    <Card hover>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl font-bold text-text-primary">${loan.requestedAmount.toLocaleString()}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColors[loan.status] ?? ''}`}>
                  {loan.status}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                <span className="font-mono">{formatAddress(loan.borrowerAddress)}</span>
                {' · '}
                {new Date(loan.createdAt).toLocaleDateString()}
                {loan.locId && <> · LOC #{loan.locId}</>}
              </p>
            </div>
          </div>
          <Button variant="default" size="sm" onClick={onAction} className="min-h-[40px]">
            Manage
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
